import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy, Loader2, Wand2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import i18n from "@/lib/i18n";
import { supabase } from "@/lib/supabase";
import { enrollCleaningLocation } from "@/lib/enrollCleaningLocation";
import { GooglePlacesAddressInput, type PlaceData } from "./GooglePlacesAddressInput";
import { LegalEntityNipField } from "@/components/legal-entity/LegalEntityNipField";
import { HOUSING_KINDS, COMPANY_KINDS } from "@/lib/legalEntityMessages";
import { LegalEntityApiError, type LegalEntityPublic } from "@/lib/legalEntityApi";
import { useRole } from "@/contexts/RoleContext";
import { validatePIN } from "@/lib/validation";
import ValidationChecklist from "@/components/ValidationChecklist";

type WorkerFormRole = "owner" | "coordinator" | "cleaner";

function generatePIN(): string {
  // Generate random 6-digit PIN
  // Avoid simple sequences by ensuring at least some variation
  let pin = "";
  const digits = "0123456789";
  
  // Generate 6 random digits
  for (let i = 0; i < 6; i++) {
    pin += digits[Math.floor(Math.random() * digits.length)];
  }
  
  // If it's a simple sequence, regenerate (safety check)
  const validation = validatePIN(pin);
  if (!validation.allValid) {
    // Retry once more
    pin = "";
    for (let i = 0; i < 6; i++) {
      pin += digits[Math.floor(Math.random() * digits.length)];
    }
  }
  
  return pin;
}

/** Generates suggested login: first letter of first name + "." + last name (lowercase, no Polish chars). e.g. "Marek Józefiak" -> "m.jozefiak" */
function buildLoginPreview(firstName: string, lastName: string): string {
  const first = (firstName || "").trim();
  const last = (lastName || "").trim();
  if (!first || !last) return "";
  const normalize = (s: string) =>
    s
      .toLowerCase()
      .replace(/ą/g, "a")
      .replace(/ć/g, "c")
      .replace(/ę/g, "e")
      .replace(/ł/g, "l")
      .replace(/ń/g, "n")
      .replace(/ó/g, "o")
      .replace(/ś/g, "s")
      .replace(/ź|ż/g, "z")
      .replace(/\s+/g, "");
  const initial = normalize(first)[0] ?? "";
  const lastPart = normalize(last);
  return initial ? `${initial}.${lastPart}` : "";
}

function loginFromStaffEmail(email: string | null | undefined): string | null {
  if (!email) return null;
  const m = email.match(/^(.+)@staff\.domio\.com\.pl$/i);
  return m ? m[1] : null;
}

async function resolveUniqueSlug(baseSlug: string): Promise<string> {
  const normalized =
    baseSlug
      .toLowerCase()
      .replace(/[^a-z0-9.]/g, "")
      .replace(/^\.+|\.+$/g, "") || "user";

  for (let n = 0; n < 100; n++) {
    const candidate = n === 0 ? normalized : `${normalized}${n}`;
    const { data, error } = await supabase.rpc("check_slug_available", {
      p_slug: candidate,
    });
    if (error) {
      console.warn("[resolveUniqueSlug] check_slug_available:", error);
      return `${normalized}${Math.floor(Math.random() * 9000) + 1000}`;
    }
    if (data === true) return candidate;
  }
  throw new Error(i18n.t("admin.modals.slugFailed"));
}

export interface NewPropertyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  onSuccess: () => void;
}

export function NewPropertyModal({
  open,
  onOpenChange,
  orgId,
  onSuccess,
}: NewPropertyModalProps) {
  const { t } = useTranslation();
  const [address, setAddress] = React.useState("");
  const [googlePlaceId, setGooglePlaceId] = React.useState<string | null>(null);
  const [lat, setLat] = React.useState<number | null>(null);
  const [lng, setLng] = React.useState<number | null>(null);
  const [contractor, setContractor] = React.useState<LegalEntityPublic | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setAddress("");
      setGooglePlaceId(null);
      setLat(null);
      setLng(null);
      setContractor(null);
    }
  }, [open]);

  const handlePlaceSelect = React.useCallback((data: PlaceData) => {
    setAddress(data.address);
    setGooglePlaceId(data.google_place_id);
    setLat(data.latitude);
    setLng(data.longitude);
  }, []);

  const handleSubmit = async () => {
    if (!address.trim()) {
      toast.error(t("admin.modals.addressRequired"));
      return;
    }
    
    if (!googlePlaceId) {
      toast.error(t("admin.modals.placesRequired"));
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await enrollCleaningLocation({
        orgId,
        address,
        googlePlaceId,
        latitude: lat,
        longitude: lng,
        legalEntityId: contractor?.id ?? null,
      });

      if (result.status === "duplicate") {
        toast.error(t("admin.modals.propertyExists", { address: result.address }));
        setIsSubmitting(false);
        return;
      }

      toast.success(
        result.status === "enrolled"
          ? t("admin.modals.propertyAddedCleaning")
          : t("admin.modals.propertyAdded")
      );
      if (result.contractorRecommended) {
        toast.info(t("admin.modals.contractorRecommended"));
      }
      setAddress("");
      setGooglePlaceId(null);
      setLat(null);
      setLng(null);
      setContractor(null);
      onOpenChange(false);
      onSuccess();
    } catch (e) {
      console.error("NewPropertyModal:", e);
      const errorMessage =
        e instanceof LegalEntityApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : t("admin.modals.addPropertyError");
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onPointerDownOutside={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest(".pac-container")) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{t("admin.dashboard.newProperty")}</DialogTitle>
          <DialogDescription>
            {t("admin.modals.newPropertyDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>{t("admin.modals.address")}</Label>
            <GooglePlacesAddressInput
              value={address}
              onChange={setAddress}
              onPlaceSelect={handlePlaceSelect}
              placeholder={t("admin.modals.searchAddress")}
            />
          </div>
          <LegalEntityNipField
            orgId={orgId}
            value={contractor}
            onChange={setContractor}
            allowedKinds={[...HOUSING_KINDS, ...COMPANY_KINDS]}
            flags={{ isCleaning: true }}
            optionalHint={t("admin.modals.optionalContractorHint")}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("admin.modals.adding")}
              </>
            ) : (
              t("admin.modals.add")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export interface NewWorkerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  onSuccess: () => void;
}

export function NewWorkerModal({
  open,
  onOpenChange,
  orgId: _orgIdProp,
  onSuccess,
}: NewWorkerModalProps) {
  const { t } = useTranslation();
  const { currentRole: userRole, currentOrgId } = useRole();
  const organization = currentOrgId ? { id: currentOrgId } : null;
  const [mode, setMode] = React.useState<"hub" | "simplified">("simplified");
  const [fullName, setFullName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [loginPreview, setLoginPreview] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [selectedRole, setSelectedRole] = React.useState<WorkerFormRole>("cleaner");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Optional flag for blocking worker addition (for coordinators)
  const isAddingDisabled = false; // Can be made configurable later

  // Reset form when modal closes
  React.useEffect(() => {
    if (!open) {
      setFullName("");
      setEmail("");
      setFirstName("");
      setLastName("");
      setLoginPreview("");
      setPassword("");
      setSelectedRole("cleaner");
    }
  }, [open]);

  // Live login preview when first/last name change
  React.useEffect(() => {
    setLoginPreview(buildLoginPreview(firstName, lastName));
  }, [firstName, lastName]);

  // Set role to cleaner if user is coordinator
  React.useEffect(() => {
    if (userRole === "coordinator") {
      setSelectedRole("cleaner");
    }
  }, [userRole]);

  const handleCopy = (generatedLogin?: string) => {
    const text = generatedLogin
      ? t("admin.modals.credentialsLogin", { login: generatedLogin, password })
      : t("admin.modals.credentialsPassword", { password });
    navigator.clipboard.writeText(text).then(
      () => toast.success(t("admin.modals.copied")),
      () => toast.error(t("admin.modals.copyFailed"))
    );
  };

  const handleGeneratePassword = () => {
    setPassword(generatePIN());
  };

  const handleSubmitHub = async () => {
    if (!email.trim()) {
      toast.error(t("admin.modals.emailRequired"));
      return;
    }

    if (!organization?.id) {
      toast.error(t("admin.modals.missingOrg"));
      return;
    }

    setIsSubmitting(true);
    try {
      const trimmedEmail = email.trim();
      console.log("Szukam użytkownika (RPC JSON) dla maila:", trimmedEmail);

      // Bez .maybeSingle() – funkcja SQL zwraca bezpośrednio json (lub null)
      const { data: searchResult, error: searchError } = await supabase.rpc(
        "get_profile_by_email",
        { target_email: trimmedEmail }
      );

      if (searchError) {
        console.error("BŁĄD RPC:", searchError);
        toast.error(t("admin.modals.dbError"));
        return;
      }

      if (!searchResult) {
        console.warn("RPC zwróciło null - brak użytkownika.");
        toast.error(t("admin.modals.userNotFound"));
        return;
      }

      console.log("Znaleziono użytkownika:", searchResult);

      const userData = searchResult as { id: string; full_name: string | null; email: string | null };
      const existingUser = {
        id: userData.id,
        full_name: userData.full_name,
        email: userData.email,
      };

      // 2. Sprawdzamy czy już jest w organizacji
      const { data: existingMember } = await supabase
        .from("memberships")
        .select("id")
        .eq("user_id", existingUser.id)
        .eq("org_id", organization.id)
        .maybeSingle();

      if (existingMember) {
        toast.error(t("admin.modals.alreadyMember"));
        return;
      }

      // Walidacja imienia (Frontend)
      if (!fullName || fullName.trim().length < 2) {
        toast.error(t("admin.modals.staffCardNameRequired"));
        setIsSubmitting(false);
        return;
      }

      // 3. Linkowanie z użyciem Imienia wpisanego przez Admina
      const { error: linkError } = await supabase.rpc("link_user_to_org", {
        target_user_id: existingUser.id,
        target_org_id: organization.id,
        target_role: selectedRole,
        target_full_name: fullName.trim(),
        target_email: trimmedEmail,
      });

      if (linkError) {
        console.error("Błąd linkowania (RPC):", linkError);
        throw linkError;
      }

      toast.success(t("admin.modals.workerAdded", { name: fullName.trim() }));
      setFullName("");
      setEmail("");
      onOpenChange(false);
      onSuccess();
    } catch (err: unknown) {
      console.error("Błąd dodawania z Huba:", err);
      const errObj = err as { message?: string; details?: string };
      const message = errObj?.message ?? errObj?.details ?? t("errors.unknownError");
      toast.error(t("admin.modals.errorWithMessage", { message }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitSimplified = async () => {
    if (!organization?.id) {
      toast.error(t("admin.modals.missingOrg"));
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      toast.error(t("admin.modals.nameRequired"));
      return;
    }
    if (!password.trim()) {
      toast.error(t("admin.modals.pinRequired"));
      return;
    }
    const pinValidation = validatePIN(password.trim());
    if (!pinValidation.allValid) {
      const errors: string[] = [];
      if (!pinValidation.isSixDigits) errors.push(t("admin.modals.pinSixDigits"));
      if (!pinValidation.isNotSimple) errors.push(t("admin.modals.pinNotSimple"));
      toast.error(t("admin.modals.pinRequirements", { details: errors.join(", ") }));
      return;
    }
    if (userRole === "coordinator" && isAddingDisabled) {
      toast.error(t("admin.modals.noPermissionAddWorkers"));
      return;
    }

    const pin = password.trim();
    const slugBase = loginPreview || buildLoginPreview(firstName, lastName);
    if (!slugBase) {
      toast.error(t("admin.modals.loginGenerateFailed"));
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error(t("admin.modals.noSession"));
      }

      const slug = await resolveUniqueSlug(slugBase);
      const payload = {
        slug,
        pin,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        orgId: organization.id,
        role: selectedRole,
        membershipRole: selectedRole,
      };

      const { data, error } = await supabase.functions.invoke("create-worker", {
        body: payload,
      });

      const payloadError =
        data && typeof data === "object" && "error" in data
          ? String((data as { error?: string }).error ?? "")
          : "";
      if (error || payloadError) {
        throw new Error(payloadError || (error instanceof Error ? error.message : t("admin.modals.createWorkerError")));
      }

      const created = data as { userId?: string; user_id?: string; email?: string; login?: string } | null;
      const userId = created?.userId || created?.user_id;
      if (!userId) {
        throw new Error(t("admin.modals.createWorkerNoId"));
      }

      const createdLogin =
        created?.login || loginFromStaffEmail(created?.email) || slug;
      toast.success(
        t("admin.modals.workerCreated", { login: createdLogin, pin }),
        { duration: 10000 }
      );
      setFirstName("");
      setLastName("");
      setLoginPreview("");
      setPassword("");
      setSelectedRole("cleaner");
      onOpenChange(false);
      onSuccess();
    } catch (err: unknown) {
      console.error("Błąd krytyczny:", err);
      const message =
        err instanceof Error
          ? err.message
          : typeof (err as { message?: string })?.message === "string"
            ? (err as { message: string }).message
            : t("admin.dashboard.unexpectedError");
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = mode === "hub" ? handleSubmitHub : handleSubmitSimplified;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("admin.dashboard.newWorker")}</DialogTitle>
          <DialogDescription>
            {t("admin.modals.newWorkerDescription")}
          </DialogDescription>
        </DialogHeader>
        <Tabs value={mode} onValueChange={(v) => setMode(v as "hub" | "simplified")}>
          <TabsList>
            <TabsTrigger value="hub">Hub</TabsTrigger>
            <TabsTrigger value="simplified">{t("admin.modals.tabSimplified")}</TabsTrigger>
          </TabsList>
          <TabsContent value="hub" forceMount className="space-y-4 pt-4 data-[state=inactive]:hidden">
            <div className="grid gap-2">
              <Label>{t("admin.modals.fullName")}</Label>
              <Input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={t("admin.modals.fullNamePlaceholder")}
                disabled={isSubmitting}
              />
            </div>
            <div className="grid gap-2">
              <Label>{t("admin.modals.email")}</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                disabled={isSubmitting}
              />
            </div>
            <div className="grid gap-2">
              <Label>{t("admin.modals.role")}</Label>
              <Select
                value={selectedRole}
                onValueChange={(value) => setSelectedRole(value as WorkerFormRole)}
                disabled={isSubmitting || userRole === "coordinator" || isAddingDisabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("admin.modals.selectRole")} />
                </SelectTrigger>
                <SelectContent>
                  {userRole === "owner" && (
                    <>
                      <SelectItem value="owner">{t("roles.owner")}</SelectItem>
                      <SelectItem value="coordinator">{t("roles.coordinator")}</SelectItem>
                      <SelectItem value="cleaner">{t("admin.modals.roleWorker")}</SelectItem>
                    </>
                  )}
                  {userRole === "coordinator" && (
                    <SelectItem value="cleaner">{t("admin.modals.roleWorker")}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </TabsContent>
          <TabsContent value="simplified" forceMount className="space-y-4 pt-4 data-[state=inactive]:hidden">
            <div className="grid gap-2">
              <Label>{t("admin.modals.firstName")}</Label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={t("admin.modals.firstNamePlaceholder")}
                disabled={isSubmitting}
              />
            </div>
            <div className="grid gap-2">
              <Label>{t("admin.modals.lastName")}</Label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder={t("admin.modals.lastNamePlaceholder")}
                disabled={isSubmitting}
              />
            </div>
            <div className="grid gap-2">
              <Label>{t("admin.modals.suggestedLogin")}</Label>
              <Input
                value={loginPreview}
                readOnly
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                {t("admin.modals.loginSuffixHint")}
              </p>
            </div>
            <div className="grid gap-2">
              <Label>{t("admin.modals.role")}</Label>
              <Select
                value={selectedRole}
                onValueChange={(value) => setSelectedRole(value as WorkerFormRole)}
                disabled={isSubmitting || userRole === "coordinator" || isAddingDisabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("admin.modals.selectRole")} />
                </SelectTrigger>
                <SelectContent>
                  {userRole === "owner" && (
                    <>
                      <SelectItem value="owner">{t("roles.owner")}</SelectItem>
                      <SelectItem value="coordinator">{t("roles.coordinator")}</SelectItem>
                      <SelectItem value="cleaner">{t("admin.modals.roleWorker")}</SelectItem>
                    </>
                  )}
                  {userRole === "coordinator" && (
                    <SelectItem value="cleaner">{t("admin.modals.roleWorker")}</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {userRole === "coordinator" && (
                <p className="text-sm text-muted-foreground">
                  {t("admin.modals.coordinatorsWorkersOnly")}
                </p>
              )}
            </div>
            <div className="grid gap-2">
              <Label>{t("admin.modals.pin")}</Label>
              <div className="flex gap-2">
                <Input 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("admin.modals.pinPlaceholder")}
                  disabled={isSubmitting}
                  maxLength={6}
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="icon" 
                  onClick={handleGeneratePassword} 
                  title={t("admin.modals.generatePin")}
                  disabled={isSubmitting}
                >
                  <Wand2 className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => handleCopy()}
                  title={t("admin.modals.copyPinTitle")}
                  disabled={isSubmitting || !password}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <ValidationChecklist value={password} type="pin" />
            </div>
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t("common.cancel")}
          </Button>
          {mode === "simplified" ? (
            <>
              <Button variant="outline" onClick={() => handleCopy()} disabled={isSubmitting || !password}>
                <Copy className="mr-2 h-4 w-4" />
                {t("admin.modals.copyPin")}
              </Button>
              <Button
                onClick={handleSubmitSimplified}
                disabled={
                  isSubmitting ||
                  !firstName.trim() ||
                  !lastName.trim() ||
                  !password.trim() ||
                  !validatePIN(password.trim()).allValid ||
                  (userRole === "coordinator" && isAddingDisabled)
                }
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("common.saveProgress")}
                  </>
                ) : (
                  t("common.save")
                )}
              </Button>
            </>
          ) : (
            <Button onClick={handleSubmit} disabled={isSubmitting || !email.trim()}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("common.saveProgress")}
                </>
              ) : (
                t("admin.modals.addToTeam")
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export interface OneTimeTaskModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  locations: { id: string; address: string }[];
  sectionsByLocation: Record<string, { id: string; name: string; assigned_staff_id: string | null }[]>;
  onSuccess: () => void;
}

export function OneTimeTaskModal({
  open,
  onOpenChange,
  orgId,
  locations,
  sectionsByLocation,
  onSuccess,
}: OneTimeTaskModalProps) {
  const { t } = useTranslation();
  const [locationId, setLocationId] = React.useState<string>("");
  const [sectionId, setSectionId] = React.useState<string>("");
  const [taskName, setTaskName] = React.useState("");
  const [requiresPhoto, setRequiresPhoto] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmit = async () => {
    if (!locationId || !sectionId || !taskName.trim()) return;

    setIsSubmitting(true);
    try {
      // 1. Dodaj zadanie jednorazowe - dostępne od razu (dzisiaj)
      // Inicjalizuj checklist jako tablicę z jednym obiektem dla spójności z widokiem pracownika
      const checklistItem = {
        id: crypto.randomUUID(),
        name: taskName,
        completed: false,
        requires_photo: requiresPhoto,
        section_id: sectionId,
      };
      
      const { data: taskData, error: taskError } = await supabase
        .from("cleaning_tasks")
        .insert({
          org_id: orgId,
          location_id: locationId,
          section_id: sectionId,
          coordinator_notes: taskName, // Nazwa zadania w coordinator_notes
          task_type: "coordinator_single", // Typ zadania jednorazowego
          status: "pending",
          assigned_staff_id: null,
          scheduled_at: new Date().toISOString(), // Pełny timestamp - dostępne od razu
          checklist: [checklistItem],
        })
        .select()
        .single();

      if (taskError) throw taskError;

      toast.success(t("admin.modals.taskAdded"));
      onSuccess?.();
      onOpenChange(false);
      
      // Reset formularza
      setTaskName("");
      setRequiresPhoto(false);
    } catch (error: any) {
      console.error("Error creating task:", error);
      toast.error(t("admin.modals.taskAddFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("admin.modals.oneTimeTitle")}</DialogTitle>
          <DialogDescription>
            {t("admin.modals.oneTimeDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>{t("admin.modals.location")}</Label>
            <Select value={locationId} onValueChange={setLocationId}>
              <SelectTrigger>
                <SelectValue placeholder={t("admin.modals.selectLocation")} />
              </SelectTrigger>
              <SelectContent>
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>
                    {loc.address}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>{t("admin.modals.area")}</Label>
            <Select
              value={sectionId}
              onValueChange={setSectionId}
              disabled={!locationId}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("admin.modals.selectArea")} />
              </SelectTrigger>
              <SelectContent>
                {(sectionsByLocation[locationId] || []).filter(s => s.id).map((section) => (
                  <SelectItem key={section.id} value={section.id}>
                    {section.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>{t("admin.modals.taskName")}</Label>
            <Input
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              placeholder={t("admin.modals.taskNamePlaceholder")}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="requires-photo"
              checked={requiresPhoto}
              onCheckedChange={(checked) => setRequiresPhoto(checked === true)}
            />
            <Label
              htmlFor="requires-photo"
              className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t("admin.modals.requirePhotoOnFinish")}
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !locationId || !sectionId || !taskName.trim()}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("admin.modals.adding")}
              </>
            ) : (
              t("admin.modals.add")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}